import { useState, useEffect, useMemo, useRef, useCallback, memo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useStrings } from '../locales';
import allDomainCards, { DomainCard } from '../character/content/domains/cards';
import { useContentVersion } from '../hooks/ContentVersionContext';
import { pickVersion, sourceLabel, matchesSource, SOURCE_OPTIONS } from '../character/content/sources';
import { fuzzyFilter } from '../search/fuzzySearch';
import { DomainIcon } from '../character/cards/DomainIcon';
import {
  domains,
  DOMAIN_COLORS,
  CLASS_DOMAINS,
  ALL_DOMAINS,
  ALL_TYPES,
  ALL_LEVELS,
  ALL_CLASSES,
  cardAnatomy,
  vaultRules,
} from '../character/content/domains/data';
import { X, ChevronLeft, ChevronRight, RotateCcw, Download } from 'lucide-react';
import { useNewSourceFilter } from '../hooks/useNewSourceFilter';
import '../components/CardSheet.css';
import { SwipeHint } from '../components/CardSheet';
import { downloadPng } from '../utils/downloadPng';
import { DomainCardSheet, ZapIcon } from '../character/cards/DomainCardSheet';
import './Domains.css';

/* ── Reset button ── */
function ResetBtn({ dirty, onClick }: { dirty: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      disabled={!dirty}
      title="Reset all filters"
      className={`reset-btn${dirty ? ' dirty' : ''}`}
    >
      <RotateCcw size={13} /> Reset
    </button>
  );
}

/* ── Multi-select filter helpers ── */
function toggleFilter(current: Set<string>, value: string): Set<string> {
  if (value === 'All') return new Set(['All']);
  const next = new Set(current);
  next.delete('All');
  if (next.has(value)) {
    next.delete(value);
    if (next.size === 0) next.add('All');
  } else {
    next.add(value);
  }
  return next;
}

function passesFilter(set: Set<string>, value: string): boolean {
  return set.has('All') || set.has(value);
}

/* ── Card effect-type tags ── */
const SHOW_CARD_TAGS = false; // set to true to show DMG/SUP/UTIL tags

const CARD_TAGS = {
  dmg: { short: 'DMG', label: 'Damage', color: '#e0634e' },
  sup: { short: 'SUP', label: 'Support', color: '#5aad7e' },
  util: { short: 'UTIL', label: 'Utility', color: '#9a9a9a' },
} as const;

function getCardTag(text: string) {
  const t = text.toLowerCase();
  if (/\bdamage\b/.test(t)) return CARD_TAGS.dmg;
  if (/\bheal(ing|s|ed)?\b|restore.*hit point|recover.*hp|reduce.*damage|reduce incoming/.test(t)) return CARD_TAGS.sup;
  return CARD_TAGS.util;
}


/* ── Card grid item — memo so arrow-key navigation doesn't re-render all 189 cards ── */
const CardGridItem = memo(function CardGridItem({
  card,
  color,
  onOpen,
}: {
  card: DomainCard;
  color: string;
  onOpen: (card: DomainCard) => void;
}) {
  const tag = SHOW_CARD_TAGS ? getCardTag(card.text) : null;
  return (
    <button
      type="button"
      className="domain-card-item"
      data-domain={card.domain.toLowerCase()}
      style={{ '--domain-color': color } as React.CSSProperties}
      onClick={() => onOpen(card)}
    >
      <DomainIcon domain={card.domain} className="domain-card-item__art" />
      <div className="domain-card-item__top">
        <div className="domain-card-item__left">
          <span className="domain-card-item__level">{card.level}</span>
          <div className="domain-card-item__name">{card.name}</div>
        </div>
        {card.recallCost > 0 && (
          <span className="domain-card-item__recall">
            <ZapIcon size={9} />{card.recallCost}
          </span>
        )}
      </div>
      <div className="domain-card-item__bottom">
        <span className={`domain-badge domain-${card.domain.toLowerCase()}`}>{card.domain}</span>
        <span className="domain-card-item__type">{card.type}</span>
        {tag && <span className="domain-card-item__tag" style={{ color: tag.color }}>· {tag.short}</span>}
      </div>
    </button>
  );
});

/* ── Card detail modal ── */
interface CardModalProps {
  card: DomainCard | null;
  cards: DomainCard[];
  onClose: () => void;
  onNavigate: (card: DomainCard) => void;
}

function CardModal({ card, cards, onClose, onNavigate }: CardModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef(0);
  const isDragging = useRef(false);

  const idx = card ? cards.indexOf(card) : -1;

  const goTo = useCallback((delta: -1 | 1) => {
    onNavigate(cards[(idx + delta + cards.length) % cards.length]);
  }, [cards, idx, onNavigate]);

  /* Focus trap + arrow-key navigation */
  useEffect(() => {
    if (!card) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(-1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); goTo(1); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [card, onClose, goTo]);

  const handleDownload = useCallback(() => {
    if (modalRef.current && card) downloadPng(modalRef.current, `${card.name.replace(/\s+/g, '-').toLowerCase()}-${card.domain.toLowerCase()}.png`);
  }, [card, modalRef]);

  if (!card) return null;

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    isDragging.current = true;
    if (modalRef.current) modalRef.current.style.transition = 'none';
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging.current || !modalRef.current) return;
    const dx = e.touches[0].clientX - touchStartX.current;
    const clamped = Math.max(-60, Math.min(60, dx));
    modalRef.current.style.transform = `translateX(${clamped}px)`;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!isDragging.current || !modalRef.current) return;
    isDragging.current = false;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    modalRef.current.style.transition = '';
    modalRef.current.style.transform = '';
    if (dx < -60) goTo(1);
    else if (dx > 60) goTo(-1);
  };

  return (
    <div
      className="card-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="card-modal-title"
      onClick={onClose}
      onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
    >
      {/* ── Prev arrow ── */}
      <button
        className="card-nav-btn card-nav-btn--prev"
        onClick={(e) => { e.stopPropagation(); goTo(-1); }}
        aria-label="Previous card"
      ><ChevronLeft size={20} aria-hidden="true" /></button>

      {/* ── Swipe hint (mobile only) ── */}
      <SwipeHint />

      <DomainCardSheet
        ref={modalRef}
        card={card}
        counter={`${idx + 1} / ${cards.length}`}
        titleId="card-modal-title"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Close — top centre ── */}
        <button
          className="card-sheet__close"
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); onClose(); }}
          aria-label="Close card"
        ><X size={16} /></button>
      </DomainCardSheet>

      {/* ── Download button — after card-sheet so it paints on top ── */}
      <button
        className="card-download-btn"
        onClick={(e) => { e.stopPropagation(); handleDownload(); }}
        aria-label="Download card"
        title="Download card"
      >
        <Download size={16} />
        Download
      </button>

      {/* ── Next arrow ── */}
      <button
        className="card-nav-btn card-nav-btn--next"
        onClick={(e) => { e.stopPropagation(); goTo(1); }}
        aria-label="Next card"
      ><ChevronRight size={20} aria-hidden="true" /></button>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   Main page
   ════════════════════════════════════════════════════════════════ */
export default function Domains() {
  const s = useStrings().domains;
  const [searchParams] = useSearchParams();
  const [view, setView] = useState('overview');
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const [classFilter, setClass] = useState(new Set(['All']));
  const [domainFilter, setDomain] = useState(new Set(['All']));
  const [typeFilter, setType] = useState(new Set(['All']));
  const [levelFilter, setLevel] = useState(new Set(['All']));
  const [sourceFilter, setSource] = useState('All');
  const { glows, onSourceClick } = useNewSourceFilter('/domains');
  const [version] = useContentVersion();
  // Dread has a Void and a Hope & Fear set; show only the chosen one.
  const domainCards = useMemo(() => pickVersion(allDomainCards, version, (c) => c.domain), [version]);
  const domainSource = (domain: string) => domainCards.find((c) => c.domain === domain)?.source;

  const [modalCard, setModalCard] = useState<DomainCard | null>(null);

  const isDefault = (s: Set<string>) => s.size === 1 && s.has('All');
  const isDirty = !!search || !isDefault(classFilter) || !isDefault(domainFilter) ||
    !isDefault(typeFilter) || !isDefault(levelFilter) || sourceFilter !== 'All';

  const resetAll = () => {
    setSearch('');
    setClass(new Set(['All']));
    setDomain(new Set(['All']));
    setType(new Set(['All']));
    setLevel(new Set(['All']));
    setSource('All');
  };

  useEffect(() => {
    if (searchParams.get('view') === 'cards') setView('cards');
    const cardName = searchParams.get('card');
    if (cardName) {
      const found = domainCards.find((c) => c.name.toLowerCase() === cardName.toLowerCase());
      if (found) { setView('cards'); setModalCard(found); }
    }
  }, [searchParams, domainCards]);

  const handleClassChange = (cls: string) => {
    const next = toggleFilter(classFilter, cls);
    setClass(next);
    if (!next.has('All')) setDomain(new Set(['All']));
  };

  const handleDomainChange = (dom: string) => {
    const next = toggleFilter(domainFilter, dom);
    setDomain(next);
    if (!next.has('All')) setClass(new Set(['All']));
  };

  const openCard = useCallback((card: DomainCard) => setModalCard(card), []);

  const filtered = useMemo(() => {
    const base = domainCards.filter((c) => {
      if (!classFilter.has('All')) {
        const allowed = new Set([...classFilter].flatMap((cls) => (CLASS_DOMAINS as Record<string, string[]>)[cls] || []));
        if (!allowed.has(c.domain)) return false;
      } else if (!domainFilter.has('All') && !domainFilter.has(c.domain)) {
        return false;
      }
      if (!passesFilter(typeFilter, c.type)) return false;
      if (!passesFilter(levelFilter, String(c.level))) return false;
      return matchesSource(sourceFilter, c.source);
    });
    return fuzzyFilter(base, search, (c) => c.text, (c) => c.name);
  }, [domainCards, classFilter, domainFilter, typeFilter, levelFilter, sourceFilter, search]);

  const activeClassDomains = classFilter.has('All')
    ? null
    : [...new Set([...classFilter].flatMap((cls) => (CLASS_DOMAINS as Record<string, string[]>)[cls] || []))];

  /* ── Filter chips for mobile ── */
  const chips = [
    ...(search ? [{ label: `"${search}"`, color: 'var(--gold)', onRemove: () => setSearch('') }] : []),
    ...[...classFilter].filter(v => v !== 'All').map(v => ({ label: v, color: 'var(--gold)', onRemove: () => handleClassChange(v) })),
    ...[...domainFilter].filter(v => v !== 'All').map(v => ({ label: v, color: (DOMAIN_COLORS as Record<string, string>)[v], onRemove: () => handleDomainChange(v) })),
    ...[...typeFilter].filter(v => v !== 'All').map(v => ({ label: v, color: 'var(--gold)', onRemove: () => setType(p => toggleFilter(p, v)) })),
    ...[...levelFilter].filter(v => v !== 'All').map(v => ({ label: `Lvl ${v}`, color: 'var(--gold)', onRemove: () => setLevel(p => toggleFilter(p, v)) })),
    ...(sourceFilter !== 'All' ? [{ label: sourceFilter, color: 'var(--gold)', onRemove: () => setSource('All') }] : []),
  ];

  return (
    <div className="page-content">
      <CardModal card={modalCard} cards={filtered} onClose={() => setModalCard(null)} onNavigate={setModalCard} />

      <div className="page-header">
        <h1>{s.title}</h1>
        <p>{s.desc}</p>
      </div>

      {/* ── View tabs ── */}
      <div className="domain-view-tabs" role="tablist">
        <button
          className={`domain-view-tab${view === 'overview' ? ' active' : ''}`}
          onClick={() => setView('overview')}
          role="tab"
          aria-selected={view === 'overview'}
          tabIndex={view === 'overview' ? 0 : -1}
        >
          Domain Overview
        </button>
        <button
          className={`domain-view-tab${view === 'cards' ? ' active' : ''}`}
          onClick={() => setView('cards')}
          role="tab"
          aria-selected={view === 'cards'}
          tabIndex={view === 'cards' ? 0 : -1}
        >
          Card Library ({domainCards.length})
        </button>
      </div>

      {/* ════════ OVERVIEW ════════ */}
      {view === 'overview' && (
        <>
          <div className="domain-system-box">
            <h3>Domain Card System</h3>
            <p>
              Each class has a <strong>Loadout</strong>, a set of domain cards
              you keep active, and a <strong>Vault</strong> of extra cards.
              Cards are leveled (1–10) and cost Recall to swap between Loadout and Vault during rests.
            </p>
          </div>

          <div className="filter-bar">
            <span className="filter-label">Source</span>
            {SOURCE_OPTIONS.map((src) => (
              <button
                key={src}
                className={`filter-btn${sourceFilter === src ? ' active' : ''}${glows(src) ? ' new-source-glow' : ''}`}
                onClick={() => { setSource(src); onSourceClick(src); }}
                aria-pressed={sourceFilter === src}
              >
                {src}
              </button>
            ))}
          </div>

          <div className="card-grid">
            {domains.filter((domain) => matchesSource(sourceFilter, domainSource(domain.name))).map((domain) => (
              <div key={domain.name} className="card domain-overview-card" id={domain.name.toLowerCase()}>
                <DomainIcon domain={domain.cssClass} className="domain-overview-card__art" />
                <div className="card-title">
                  <span className={`domain-badge domain-${domain.cssClass}`}>{domain.name}</span>
                </div>
                <div>
                  <div className="domain-overview-classes-label">Primary Classes</div>
                  <div className="card-tags">
                    {domain.classes.map((c) => (
                      <Link key={c} to={`/classes/${c.toLowerCase()}`} className={`tag domain-${domain.cssClass} domain-tag-link`}>{c}</Link>
                    ))}
                  </div>
                </div>
                <div className="card-body domain-overview-card-body">{domain.description}</div>
                <span className="source-label">{sourceLabel(domainSource(domain.name))}</span>
                {/* <div className="domain-overview-section">
                  <div className="domain-overview-themes-label">Themes</div>
                  <div className="card-tags">
                    {domain.themes.map((t) => <span key={t} className="tag">{t}</span>)}
                  </div>
                </div> */}
                {/* <div>
                  <div className="domain-overview-classes-label">Primary Classes</div>
                  <div className="card-tags">
                    {domain.classes.map((c) => (
                      <Link key={c} to={`/classes/${c.toLowerCase()}`} className="tag tag-purple domain-tag-link">{c}</Link>
                    ))}
                  </div>
                </div> */}
              </div>
            ))}
          </div>

          <div className="divider domain-divider" />

          <h2 className="domain-overview-h2">Domain Card Rules</h2>
          <h3 className="domain-overview-section-title">Card Anatomy</h3>
          <div className="domain-anatomy-grid">
            {cardAnatomy.map(({ label, text }) => (
              <div key={label} className="domain-anatomy-item">
                <div className="domain-anatomy-item__label">{label}</div>
                <div className="domain-anatomy-item__text">{text}</div>
              </div>
            ))}
          </div>

          <h3 className="domain-overview-section-title">Loadout &amp; Vault</h3>
          <div className="domain-vault-box">
            {vaultRules.map((item, i) => <div key={i}>{item}</div>)}
          </div>
        </>
      )}

      {/* ════════ CARD LIBRARY ════════ */}
      {view === 'cards' && (
        <>
          <input
            type="text"
            id="card-search"
            name="domains-cards-search"
            aria-label="Search domain cards"
            placeholder="Search cards…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-input"
          />
          <div className="card-library-layout">

            {/* Mobile filter toggle row */}
            <div className="filter-toggle-row">
              <button
                className="filter-toggle-btn"
                onClick={() => setShowFilters(f => !f)}
                aria-expanded={showFilters}
                aria-controls="card-library-filters-panel"
              >
                <span>⚙ Filters{isDirty ? ' •' : ''}</span>
                <span>{showFilters ? '▲' : '▼'}</span>
              </button>
              <ResetBtn dirty={isDirty} onClick={resetAll} />
            </div>

            {/* Mobile active filter chips */}
            {chips.length > 0 && (
              <div className="mobile-filter-chips">
                {chips.map(({ label, color, onRemove }) => (
                  <span key={label} className="filter-chip" style={{ '--chip-color': color || 'var(--gold)' }}>
                    {label}
                    <button className="filter-chip-remove" onClick={onRemove}>×</button>
                  </span>
                ))}
              </div>
            )}

            {/* ── Left panel ── */}
            <div id="card-library-filters-panel" className={`card-library-filters${showFilters ? '' : ' card-library-filters--hidden'}`}>
              <div className="card-library-filters__header">
                <span className="filter-label">Filters</span>
                <span className="hide-mobile"><ResetBtn dirty={isDirty} onClick={resetAll} /></span>
              </div>

              {/* Count */}
              <div className="card-library-filters__count">
                <div aria-live="polite" aria-atomic="true">{filtered.length} of {domainCards.length}</div>
                {activeClassDomains && (
                  <div className="card-library-filters__active-domains">{activeClassDomains.join(' + ')}</div>
                )}
              </div>

              {/* Class */}
              <div className="card-library-filters__section">
                <span className="filter-label card-library-filters__section-label">Class</span>
                <div className="card-library-filters__pill-group">
                  {ALL_CLASSES.map((cls) => {
                    const active = cls === 'All' ? classFilter.has('All') : classFilter.has(cls);
                    return (
                      <button key={cls} onClick={() => handleClassChange(cls)}
                        className={`filter-btn-gold${active ? ' active' : ''}`}
                        aria-pressed={active}>
                        {cls}
                        {(CLASS_DOMAINS as Record<string, string[]>)[cls] && (
                          <span className="cls-filter-domains">{(CLASS_DOMAINS as Record<string, string[]>)[cls].join('·')}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Domain */}
              <div className="card-library-filters__section">
                <span className="filter-label card-library-filters__section-label">Domain</span>
                <div className="card-library-filters__pill-group">
                  {ALL_DOMAINS.map((d) => {
                    const active = d === 'All'
                      ? domainFilter.has('All') && classFilter.has('All')
                      : domainFilter.has(d) && classFilter.has('All');
                    return (
                      <button key={d} onClick={() => handleDomainChange(d)}
                        className={`filter-btn-domain${active ? ' active' : ''}${d === 'All' ? ' no-color' : ''}`}
                        style={d !== 'All' ? { '--domain-color': (DOMAIN_COLORS as Record<string, string>)[d] } : undefined}
                        aria-pressed={active}>
                        {d}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Type, mobile only */}
              <div className="card-library-filters__section show-mobile-only">
                <span className="filter-label card-library-filters__section-label">Type</span>
                <div className="card-library-filters__pill-group">
                  {ALL_TYPES.map((t) => (
                    <button key={t} onClick={() => setType(toggleFilter(typeFilter, t))}
                      className={`filter-btn-gold${typeFilter.has(t) ? ' active' : ''}`}
                      aria-pressed={typeFilter.has(t)}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>

            </div>

            {/* ── Right panel ── */}
            <div className="card-library-right">
              <div className="card-library-bar">
                {/* Type, desktop only */}
                <div className="card-library-bar__group hide-mobile">
                  <span className="filter-label">Type</span>
                  {ALL_TYPES.map((t) => (
                    <button key={t}
                      className={`filter-btn${typeFilter.has(t) ? ' active' : ''}`}
                      onClick={() => setType(toggleFilter(typeFilter, t))}
                      aria-pressed={typeFilter.has(t)}>
                      {t}
                    </button>
                  ))}
                </div>
                <div className="card-library-divider hide-mobile" />
                {/* Level */}
                <div className="card-library-bar__group">
                  <span className="filter-label">Level</span>
                  {ALL_LEVELS.map((l) => (
                    <button key={l}
                      className={`filter-btn${levelFilter.has(l) ? ' active' : ''}`}
                      onClick={() => setLevel(toggleFilter(levelFilter, l))}
                      aria-pressed={levelFilter.has(l)}>
                      {l}
                    </button>
                  ))}
                </div>
                <div className="card-library-divider" />
                {/* Source */}
                <div className="card-library-bar__group">
                  <span className="filter-label">Source</span>
                  {SOURCE_OPTIONS.map((src) => (
                    <button key={src}
                      className={`filter-btn${sourceFilter === src ? ' active' : ''}${glows(src) ? ' new-source-glow' : ''}`}
                      onClick={() => { setSource(src); onSourceClick(src); }}
                      aria-pressed={sourceFilter === src}>
                      {src}
                    </button>
                  ))}
                </div>
              </div>

              {filtered.length === 0 && (
                <div className="empty-state"><p>{s.noCardsMatch}</p></div>
              )}

              <div className="domain-card-grid">
                {filtered.map((card) => (
                  <CardGridItem
                    key={card.name}
                    card={card}
                    color={(DOMAIN_COLORS as Record<string, string>)[card.domain] || '#888'}
                    onOpen={openCard}
                  />
                ))}
              </div>
            </div>

          </div>
        </>
      )}
    </div>
  );
}